<template>
  <b-modal ref="modal" hide-footer :hide-header="!loaded" class="modal-fw modal-meal"
           :class="{ ['compact-header']: compactHeader }">
    <template slot="modal-header" v-if="loaded">
      <b-container class="user-info">
        <b-row>
          <b-col lg="3" xl="4" class="col">
            <live-input v-if="isSelectableUser" label="For" v-model="userId" as="select"
                        :options="userOptionsForSelect" @input="switchUser"/>

            <div v-else>
              <div class="text-upper font-xxs">For</div>
              <h4>{{ user.fullName }}</h4>
            </div>
          </b-col>

          <b-col lg="4" xl="4" class="font-sm col">
            <div class="text-upper font-xxs">Likes</div>
            {{ user.likes | capitalizeEach | join(', ') }}
          </b-col>

          <b-col lg="5" xl="4" class="font-sm col">
            <div class="text-upper font-xxs">Dislikes</div>
            {{ user.dislikes | capitalizeEach | join(', ') }}
          </b-col>
        </b-row>
      </b-container>

      <div :hidden="newSelected" class="meal-filters">
        <b-container>
          <b-row>
            <b-col lg="3" xl="4" class="meal-date col text-center text-lg-left mb-3 mb-lg-0">
              {{ delivery.forDeliveryAt | date('DATE_HUGE') }}
            </b-col>

            <b-col lg="4" xl="4" class="col text-center mb-3 mb-lg-0">
              <DietFilter ref="dietLevel" :diets="diets" :user="user" @change="filterItemsByDiet"/>
            </b-col>

            <b-col lg="5" xl="4" class="col text-center text-lg-right">
              <RestrictionsFilter ref="restrictionsFilter" :diets="diets" :user="user"
                                  @change="filterItemsByRestrictions"/>
            </b-col>
          </b-row>
        </b-container>
        <button class="close" @click="$refs.modal.hide()"><i class="btr bt-times"></i></button>
      </div>

      <div v-if="newSelected" class="meal-filters py-4">
        <b-container>
          <b-row>
            <b-col>
              <a href="#" @click.prevent="goBack" class="btn-sm-align">&larr; {{ newSelected.menu.name }}</a>
              <div class="float-right" v-if="!isReadOnly">
                <b-btn v-if="isReplace || isDynamicReplace" @click="confirmReplace" size="sm" variant="primary">
                  <!-- TODO: Change to "Add for..." -->
                  Replace for {{ totalPrice | cents }}
                </b-btn>
                <b-btn v-else @click="confirm" size="sm" variant="primary">Add for {{ totalPrice | cents }}</b-btn>
              </div>
              <div class="float-right" v-else>
                <b-btn size="sm" variant="primary" disabled>
                  Order is frozen
                </b-btn>
              </div>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </template>

    <b-card no-body v-if="loaded && !newSelected" class="card-container">
      <b-nav tabs slot="header" v-b-scrollspy:nav-menus="100" class="menu-tabs">
        <b-nav-item :href="`#menu${menu.id}`"
                    v-scroll-to="{ el: `#menu${menu.id}`, container: '#nav-menus' }"
                    v-for="menu in menus" :key="menu.id">
          {{ menu.name }}
        </b-nav-item>
      </b-nav>

      <b-card-body id="nav-menus" ref="menus" @scroll.passive="adjustHeader">
        <div class="container">
          <div v-for="menu in menus" :id="`menu${menu.id}`" :key="menu.id">
            <div v-for="section in menu.sections" :key="section.id" class="menu-section"
                 v-if="sectionHasItems(section, menu)">
              <div class="section-name"><span>{{ section.name }}</span></div>
              <div class="section-description">
                {{ section.description }}
              </div>

              <b-card-group class="menu-items">
                <MenuItem v-for="item in section.items" :key="item.id"
                          @select="() => chooseMeal(item.menuId, item.id)"
                          :item="item" :forUser="user"
                          :ingredientMap="ingredients"
                          :filtered="isFiltered(item, menu)"
                          :selected="isSelected(item, menu)"
                          :hidden="isFiltered(item, menu) && !isSelected(item, menu)"/>
              </b-card-group>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <b-card no-body v-else-if="loaded && newSelected" class="card-container">
      <b-card-body>
        <div class="container">
          <MenuItemDetails @totalChange="updateOptionsPrice" :item="newSelected" :ingredientsMap="ingredients"
                           :selections="userSelections"
                           :notes="selectedNotes"
                           ref="itemDetails"/>
        </div>
      </b-card-body>
    </b-card>

    <div v-else style="flex: 1">
      <h5 class="text-center mb-0">Loading...</h5>
    </div>
  </b-modal>
</template>

<script>
import { DateTime } from 'luxon'
import GLOBAL from '../../store/actions/global'
import MenuQuery from '../../api/queries/menu'
import MenuItem from '../menus/item'
import MenuItemDetails from '../menus/item-details'
import DietFilter from '../menus/diet-filter'
import RestrictionsFilter from '../menus/restrictions-filter'
import OrderQuery from '../../api/queries/order'
import DietQuery from "../../api/queries/diet";
import { capitalizeWords } from '../../lib/filters'
import { flatten } from '../../utilities/array'
import LiveInput from '../../lib/components/live-input'

let afterUpdate = null

export default {
  props: ['club', 'delivery', 'users', 'selectableUser'],

  data() {
    return {
      menus: null,
      diet: null,
      diets: null,
      ingredients: null,
      activeRestrictions: [],
      loaded: false,
      user: null,
      userId: null,
      isSelectableUser: this.selectableUser,
      isReplace: false,
      isDynamicReplace: false,
      isReadOnly: this.delivery.state === 'preordered',
      pieceToReplace: null,
      allSelected: [],
      newSelected: null,
      hasMeal: false,
      optionsPrice: 0,
      userOptionsForSelect: this.users.map(user => { return { value: user.id, text: user.fullName } }),
      compactHeader: false,
    }
  },

  computed: {
    totalPrice() {
      const item = this.newSelected
      let total = item.menu.legacy.baseItemPrice(item.id)
      if (item.menu.pmult) total = item.menu.legacy.closestPrice(total * item.menu.pmult)
      return total + this.optionsPrice
    },

    userPieces() {
      return flatten(this.delivery.orders.map(order =>
        order.pieces.filter(piece => piece.userId === this.user.id)))
    },

    userSelections() {
      if (!this.newSelected) return
      const piece = this.userPieces.find(piece =>
        piece.menuId === this.newSelected.menuId && piece.itemId === this.newSelected.id)
      return piece ? piece.selections : null
    },

    selectedNotes() {
      return (this.pieceToReplace || {}).instructions
    },
  },

  created() {
    document.addEventListener('keydown', this.handleEscape, { capture: true })
  },

  destroyed() {
    document.removeEventListener('keydown', this.handleEscape, true)
  },

  methods: {
    handleEscape(e) {
      if (e.key === 'Escape' && this.newSelected) {
        e.stopPropagation()
        this.newSelected = null
      }
    },

    open() {
      const originalUser = this.user

      return new Promise(resolve => {
        if (!this.menus) {
          this.loadMenus().then(() => {
            if (originalUser) this.switchUser(originalUser)
            resolve()
          })
        } else {
          if (originalUser) this.switchUser(originalUser)
          resolve()
        }
        this.$refs.modal.show()
      })
    },

    close() {
      this.$refs.modal.hide()
    },

    openForAdd() {
      this.isSelectableUser = this.selectableUser
      this.isReplace = false
      this.open()
    },

    openForReplace(user, piece, options) {
      this.user = user
      this.isSelectableUser = false
      this.isReplace = true
      this.pieceToReplace = piece
      this.open()
    },

    openForEdit(user, piece) {
      this.user = user
      this.isSelectableUser = false
      this.isReplace = true
      this.pieceToReplace = piece
      this.open().then(() => this.newSelected = this.findMeal(piece.menuId, piece.itemId))
    },

    filterItemsByDiet(diet) {
      this.diet = diet
    },

    filterItemsByRestrictions(activeRestrictions) {
      this.activeRestrictions = activeRestrictions
    },

    isFiltered(item, menu) {
      let dietFiltered = this.diet && this.diet.level !== null && item.dietLevel >= this.diet.level
      if (dietFiltered) return true
      return item.ingredients !== null &&
        !!this.activeRestrictions.find(restriction =>
          !!restriction.restrictedIngredients.find(restricted => item.ingredients.indexOf(restricted) > -1)
        )
    },

    sectionHasItems(section, menu) {
      return section.items.filter(item => !this.isFiltered(item, menu)).length > 0
    },

    confirm() {
      this.confirmAdditionalMeal()
        .then(() => this.confirmRestrictions().then(this.add))
    },

    confirmReplace() {
      this.confirmAdditionalMeal()
        .then(() => this.confirmRestrictions().then(this.replace))
    },

    confirmAdditionalMeal() {
      if ((this.replace && this.userPieces.length > 1) || (!this.replace && this.hasMeal)) {
        const fullName = this.user.fullName
        const date = DateTime.fromISO(this.delivery.forDeliveryAt).toLocaleString(DateTime.DATE_DAYF_MONTH)
        const message = `You are about to add an extra meal for ${fullName} on ${date}. Would you like to proceed?`
        return this.$confirm('Add extra meal?', message, { reject: false })
      }
      return new Promise(resolve => resolve())
    },

    confirmRestrictions() {
      const userId         = this.user.id,
            customizedMeal = this.customizeMeal(this.newSelected),
            { menuId, id: itemId } = customizedMeal,
            customization  = JSON.stringify(customizedMeal.customization)
      return new Promise((resolve, reject) => {
        this.$api.query('mealRestrictions', { userId, menuId, itemId, customization }, 'conflicts')
          .then(data => {
            const user = this.user.fullName
            const conflicts = (data.mealRestrictions.conflicts || []).map(conflict => capitalizeWords(conflict))
            const message = `It looks like you're trying to select a meal which doesn't match ${user}'s dietary ` +
              `restrictions: not suitable for ${conflicts.join(', ')}`
            if (conflicts.length > 0) {
              this.$confirm('Override dietary restriction?', message, { ok: 'Add anyway', cancel: 'Modify my choice' })
                .then(resolve).catch(reject)
            } else {
              resolve()
            }
          })
      })
    },

    add() {
      const meal = this.customizeMeal(this.newSelected)
      const pieceData = OrderQuery.mutation.pieceData(meal, { userId: this.user.id });
      this.$loading(() =>
        this.$api.mutate(
          'addPiece',
          `order { ${OrderQuery.mealManagement} } errors`,
          { deliveryId: this.delivery.id, ...pieceData },
        ).then(data => {
          if (data.errors) return this.$store.dispatch(GLOBAL.LOAD_ERROR, data.errors)
          this.$emit('add', data.order, pieceData)
        }))
    },

    replace() {
      const meal = this.customizeMeal(this.newSelected)
      const pieceData = OrderQuery.mutation.pieceData(meal, { oldPieceId: this.pieceToReplace.id });
      this.$loading(() =>
        this.$api.mutate(
          'replacePiece',
          `delivery { forDeliveryAt orders { ${OrderQuery.mealManagement} } } errors`,
          { deliveryId: this.delivery.id, ...pieceData },
        ).then(data => {
          if (data.errors) return this.$store.dispatch(GLOBAL.LOAD_ERROR, data.errors)
          this.$emit('replace', data.delivery, { userId: this.user.id, ...pieceData })
        }))
    },

    customizeMeal(item) {
      const customization = this.$refs.itemDetails.selectionsHash

      return {
        ...item,
        instructions: this.$refs.itemDetails.newNotes,
        customization,
      }
    },

    loadMenus() {
      const menuIds = this.delivery.orders.map(order => order.menu.id)
      const args = `ids: [${menuIds.join(',')}], clubId: ${this.club.id}`
      return new Promise(resolve => {
        this.$loading(() =>
          this.$api.query(`{ menus(${args}) { ${MenuQuery.addUserMeal} }
                             diets { ${DietQuery.full} }
                             ingredients { category ingredients } }`)
            .then(data => {
              this.diets = data.diets
              this.ingredients = data.ingredients
              this.menus = this.$legacyMenus(data.menus)
              this.switchUser(this.users[0])
              this.loaded = true
              resolve()
            }))
      })
    },

    switchUser(userOrId) {
      const user = userOrId.hasOwnProperty('id') ? userOrId : this.users.find(user => user.id === userOrId)
      this.user = user
      this.userId = user.id
      this.newSelected = null
      this.allSelected = []
      this.hasMeal = false
      this.delivery.orders.forEach(order => {
        order.pieces.forEach(piece => {
          if (piece.userId === user.id) {
            const selected = this.findMeal(piece.menuId, piece.itemId)
            if (selected) this.allSelected.push(selected)
            this.hasMeal = true
          }
        })
      })
    },

    chooseMeal(menuId, itemId) {
      this.isDynamicReplace = !!this.allSelected.find(item => item.menuId === menuId && item.id === itemId)
      if (this.isDynamicReplace) {
        this.pieceToReplace = this.userPieces.find(piece => piece.menuId === menuId && piece.itemId === itemId)
      }
      this.newSelected = this.findMeal(menuId, itemId)
    },

    findMeal(menuId, itemId) {
      const menu = this.menus.find(m => m.id === menuId)
      let found = null
      menu.sections.find(section => {
        found = section.items.find(item => item.id === itemId)
        return found
      })
      return found ? this.wrapItem(found, menu) : null
    },

    wrapItem(item, menu) {
      if (!item) return
      item.menu = menu
      item.optionSets = []
      if (item.modifiers) {
        item.optionSets = menu.optionSets.filter(set => item.modifiers.indexOf(set.modifierId) > -1)
      }
      return item
    },

    isSelected(item, menu) {
      if (this.allSelected.length === 0) return false
      return !!this.allSelected.find(selected => item.id === selected.id && menu.id === selected.menuId)
    },

    updateOptionsPrice(total) {
      this.optionsPrice = total
    },

    goBack() {
      const menuId = this.newSelected.menuId
      this.newSelected = null
      afterUpdate = function scrollToMenu() {
        this.$scrollTo(`#menu${menuId}`, 10, {container: '#nav-menus'})
      }
    },

    adjustHeader(e) {
      this.compactHeader = e.target.scrollTop > 0
    }
  },

  updated() {
    if (afterUpdate) {
      afterUpdate.apply(this)
      afterUpdate = null
    }
  },

  components: {
    MenuItem, MenuItemDetails, DietFilter, RestrictionsFilter, LiveInput,
  },
}
</script>

<style lang="scss">
.modal-meal {
  .modal-header {
    display: block;
    border: 0;
    padding: 0;

    .container {
      position: relative;
    }
  }

  .close {
    position: absolute;
    top: 34px;
    color: $primary;

    &:hover {
      color: $secondary !important;
    }

    @include media-breakpoint-down(xl) {
      right: calc(((100vw - #{map_get($container-max-widths, 'xl')}) / 2) - 24px);
    }

    @include media-breakpoint-down(lg) {
      right: calc(((100vw - #{map_get($container-max-widths, 'lg')}) / 2) - 24px);
    }

    @include media-breakpoint-down(md) {
      right: calc(((100vw - #{map_get($container-max-widths, 'md')}) / 2) - 24px);
    }

    @include media-breakpoint-down(sm) {
      right: calc(((100vw - #{map_get($container-max-widths, 'sm')}) / 2) - 24px);
    }

    @include media-breakpoint-down(xs) {
      right: 0;
      top: 0;
    }
  }

  .user-info {
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;

    .col + .col {
      border-left: 2px solid $gray-100;
    }
  }

  .modal-body {
    display: flex;
    flex-basis: 0;
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 0;

    .card-container {
      width: 100%;
      border: 0;
    }

    .card-container > .card-header {
      padding: 0;
      background-color: transparent;
      border-bottom: $nav-tabs-border-width solid $nav-tabs-border-color;
    }

    .card-container > .card-body {
      overflow-y: scroll;
      margin-left: 12px; // Custom scrollbar width
    }

    // We need a custom scrollbar so we can set its width and take it into account for the card-body positioning above
    .card-container > .card-body::-webkit-scrollbar {
      width: 12px;
      background-color: #eee;
    }

    .card-container > .card-body::-webkit-scrollbar-thumb {
      background-color: #ccc;
      
      &:hover {
        background-color: #ddd;
      }
    }

    .card-container > .card-header .nav-tabs {
      @include make-container();
      @include make-container-max-widths();
      margin-bottom: -1px;
    }
  }

  .meal-filters {
    background-color: $gray-100;
    margin-bottom: map_get($spacers, 4);
    font-weight: bold;

    .col {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }

  .restrictions-filter {
    .btn-group {
      max-width: 278px;
    }
  }

  .meal-date {
    display: flex;
    align-items: center;
  }

  @include media-breakpoint-down(md) {
    .meal-date {
      display: block;
    }
  }

  .menu-tabs {
    font-size: 26px;

    .nav-link {
      padding: 0.625rem 1.75rem;
      border-top: 3px solid transparent;
      color: $gray-400;
    }

    .nav-item {
      position: relative;
      min-width: 10rem;
    }

    .nav-item.active, .nav-item:hover {
      color: $primary;

      &:before {
        content: "Restaurant 1";
        position: absolute;
        top: -($font-size-sm / 2);
        left: 1.5rem;
        padding: 0 0.5rem;
        font-size: $font-size-xxs;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: $gray-800;
        background-color: #fff;
      }

      .nav-link {
        border-top-color: $primary;
        color: $primary;
      }
    }

    .nav-item.active:last-child:before, .nav-item:hover:last-child:before {
      content: "Restaurant 2"
    }
  }

  $item-spacing: 1%;

  .menu-section {
    width: 100% + $item-spacing; // To align section titles with menu items on the right
    margin-bottom: 2rem;
  }

  .section-name {
    position: relative;
    text-align: center;
    width: 100% - $item-spacing; // To align section titles with menu items on the right

    span {
      position: relative;
      background-color: #fff;
      padding: 0 2rem;
      font-weight: bold;
      color: $secondary;
    }

    &:before {
      position: absolute;
      content: "";
      left: 0;
      top: 50%;
      width: 100%;
      border-bottom: $card-border-width solid $card-border-color;
    }
  }

  .section-description {
    text-align: center;
    width: 100% - $item-spacing; // To align section titles with menu items on the right
    color: $gray-500;
    font-size: $font-size-sm;
    margin-bottom: map_get($spacers, 4);
  }

  .menu-items {
    margin-left: -$item-spacing;

    .card {
      flex-basis: 100%;
      margin: 0 0 1rem 0;

      @include media-breakpoint-up(sm) {
        margin: 0 $item-spacing 1rem $item-spacing !important;
        max-width: 50% - ($item-spacing * 2);
      }

      @include media-breakpoint-up(md) {
        max-width: 33.3333% - ($item-spacing * 2);
      }

      @include media-breakpoint-up(lg) {
        max-width: 25% - ($item-spacing * 2);
      }
    }
  }

  &.compact-header {
    .user-info {
      padding-top: map_get($spacers, 2);
      padding-bottom: 0;
    }

    .meal-filters {
      margin-bottom: 1.2rem;

      .col {
        padding-top: 6px;
        padding-bottom: 6px;
      }

      .col + .col {
        border-left: 2px solid #fff;
      }
    }

    .diet-filter {
      .btn img {
        width: 30px;
        height: 30px;
        margin-bottom: 0;
      }

      span {
        display: none;
      }
    }

    .restrictions-filter {
      .btn-group {
        max-width: 235px;
      }

      .btn img {
        width: 30px;
        height: 30px;
        margin-bottom: 0;
      }

      .btn span {
        display: none;
      }
    }

    .close {
      top: 17px;
    }

    .modal-body {
      padding-top: 0;
    }

    .menu-tabs {
      .nav-item {
        font-size: 22px;
      }

      .nav-item:before {
        display: none;
      }

      .nav-link {
        padding-top: 6px;
        padding-bottom: 6px;
      }
    }
  }
}
</style>
